<template>
  <div class="calculator-container tw-p-16">
    <div class="calculator-header">
      <h2>{{ mode === 'v1' ? 'Blended Fee Calculator v1' : 'Blended Fee Calculator v2' }}</h2>
    </div>

    <div class="inputs-section">
      <!-- Common input -->
      <div class="field-group">
        <label>{{ mode === 'v1' ? 'Listing Price (£)' : 'Property Price (£)' }}</label>
        <input v-model.number="price" type="number" class="calculator-input" placeholder="Enter a price" />
      </div>

      <!-- Mode specific inputs -->
      <template v-if="mode === 'v1'">
        <div class="field-group">
          <label>Payment Option</label>
          <select v-model="option" class="calculator-select" @change="toggleAdditionalInput">
            <option value="nsnf">No Sale No Fee</option>
            <option value="part">Part Prepaid</option>
            <option value="all">Full Prepaid</option>
          </select>
        </div>

        <div v-if="option === 'part'" class="field-group additional-input">
          <label>Additional Prepaid (£)</label>
          <input v-model.number="additionalPrepaid" type="number" class="calculator-input" />
        </div>
      </template>

      <template v-else>
        <div class="field-group">
          <label>Conversion Rate (%)</label>
          <input
            v-model.number="conversion"
            type="number"
            class="calculator-input"
            placeholder="100 for full conversion"
          />
        </div>
      </template>

      <div class="calculate-section">
        <button @click="calculate" :disabled="!validPrice" class="n-primary tw-w-full md:tw-w-auto">
          Calculate Fees
        </button>
      </div>
    </div>

    <!-- Results -->
    <div v-if="validPrice && showResults" class="results-section">
      <div v-if="mode === 'v1'" class="results-table">
        <!-- Blended Results -->
        <div class="table-row header-row">
          <div class="cell">Fee Type</div>
          <div class="cell text-right">Amount</div>
        </div>
        <div class="table-row">
          <div class="cell">Listing Value</div>
          <div class="cell text-right">£{{ price.toLocaleString() }}</div>
        </div>
        <div class="table-row">
          <div class="cell">Payment Option</div>
          <div class="cell text-right">{{ optionLabel }}</div>
        </div>
        <div class="table-row">
          <div class="cell">Prepaid Fee</div>
          <div class="cell text-right">£{{ prepaid.toFixed(2) }}</div>
        </div>
        <div class="table-row">
          <div class="cell">Completion Fee</div>
          <div class="cell text-right">£{{ completion.toFixed(2) }}</div>
        </div>
        <div class="table-row total-row">
          <div class="cell">Total Fee</div>
          <div class="cell text-right">£{{ total.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}</div>
        </div>
        <div class="table-row">
          <div class="cell">Representative %</div>
          <div class="cell text-right">{{ repPercentage.toFixed(2) }}%</div>
        </div>
        <div class="table-row best-option-row">
          <div class="cell">Full Prepaid (Lowest Fee)</div>
          <div class="cell text-right">
            £{{ fullPrepaidFeeTotal.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
          </div>
        </div>

        <div v-if="option === 'part'" class="savings-message part-savings">
          You saved £{{ actualSavings.toLocaleString(undefined, { minimumFractionDigits: 2 }) }} by choosing Part
          Prepaid.
          <br />
          But you could have saved an extra £{{
            possibleExtraSavings.toLocaleString(undefined, { minimumFractionDigits: 2 })
          }}
          by paying Full Prepaid.
        </div>
        <div v-else-if="option === 'nsnf'" class="savings-message nsnf-message">
          You are on the No Sale No Fee option. Paying Part or Full Prepaid can save you money.
        </div>
        <div v-else class="savings-message optimal-message">You are already on the lowest fee option.</div>
      </div>

      <div v-else class="results-table">
        <!-- v2 Results -->
        <div class="table-row header-row">
          <div class="cell">Option</div>
          <div class="cell text-right">Fixed (£)</div>
          <div class="cell text-right">% of Price</div>
          <div class="cell text-right">Total (£)</div>
        </div>
        <div v-for="opt in v2Results" :key="opt.name" class="table-row">
          <div class="cell">{{ opt.name }}</div>
          <div class="cell text-right">£{{ opt.fixed }}</div>
          <div class="cell text-right">{{ (opt.percent * 100).toFixed(2) }}%</div>
          <div class="cell text-right">£{{ opt.total.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}</div>
        </div>
      </div>
    </div>

    <div v-else-if="!validPrice" class="error-message">Please enter values and click Calculate.</div>
  </div>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
export default defineNuxtComponent({
  name: 'BlendedFeeCalculator',

  mixins: [ComponentMixin],

  data() {
    return {
      price: null as number | null,
      option: 'nsnf' as 'nsnf' | 'part' | 'all',
      additionalPrepaid: 0,
      conversion: 100,
      showResults: false,

      // Blended fee brackets
      brackets: [
        { min: 70000, nsnf: 5.71, part: 4.57, all: 3.66 },
        { min: 80000, nsnf: 5.0, part: 4.0, all: 3.2 },
        { min: 90000, nsnf: 4.44, part: 3.56, all: 2.84 },
        { min: 100000, nsnf: 4.0, part: 3.2, all: 2.56 },
        { min: 110000, nsnf: 3.64, part: 2.91, all: 2.33 },
        { min: 120000, nsnf: 3.33, part: 2.67, all: 2.13 },
        { min: 130000, nsnf: 3.08, part: 2.46, all: 1.94 },
        { min: 140000, nsnf: 2.86, part: 2.29, all: 1.83 },
        { min: 150000, nsnf: 2.67, part: 2.13, all: 1.71 },
        { min: 160000, nsnf: 2.5, part: 2.0, all: 1.6 },
        { min: 170000, nsnf: 2.35, part: 1.88, all: 1.51 },
        { min: 180000, nsnf: 2.22, part: 1.78, all: 1.42 },
        { min: 190000, nsnf: 2.11, part: 1.68, all: 1.35 },
        { min: 200000, nsnf: 2.0, part: 1.6, all: 1.28 },
        { min: 210000, nsnf: 1.9, part: 1.52, all: 1.22 },
        { min: 220000, nsnf: 1.82, part: 1.45, all: 1.16 },
      ],

      // v2 options
      v2Options: [
        { name: 'Option 1', fixed: 200, percent: 0.015 },
        { name: 'Option 2', fixed: 2200, percent: 0.01 },
        { name: 'Option 3', fixed: 4200, percent: 0.005 },
        { name: 'Option 4', fixed: 6200, percent: 0 },
      ],

      v2Results: [] as any[],
    };
  },

  computed: {
    validPrice(): boolean {
      return this.price !== null && this.price > 0;
    },

    mode() {
      return this.vars.modeText;
    },

    // Blended Calculations
    rate_nsnf(): number {
      return this.getBracketRate(this.price!, 'nsnf');
    },
    rate_part(): number {
      return this.getBracketRate(this.price!, 'part');
    },
    rate_all(): number {
      return this.getBracketRate(this.price!, 'all');
    },

    prepaid(): number {
      if (this.option === 'part') return 395 + (this.additionalPrepaid || 0);
      if (this.option === 'all') return this.price! * (this.rate_all / 100) * 1.2;
      return 0;
    },
    completion(): number {
      if (this.option === 'nsnf') {
        return this.price! * (this.rate_nsnf / 100) * 1.2;
      } else if (this.option === 'part') {
        const basePrepaid = 395;
        const nsnfFee = this.price! * (this.rate_nsnf / 100) * 1.2;
        const orangeCompletion = nsnfFee * 0.8 - basePrepaid;
        const fullPrepaidFee = (orangeCompletion + basePrepaid) * 0.8;
        if (this.prepaid >= fullPrepaidFee) return 0;
        return orangeCompletion - (this.additionalPrepaid * (basePrepaid + orangeCompletion)) / fullPrepaidFee;
      }
      return 0;
    },
    total(): number {
      return this.prepaid + this.completion;
    },
    repPercentage(): number {
      if (this.option === 'nsnf') return this.rate_nsnf;
      if (this.option === 'part') return (this.total / 1.2 / this.price!) * 100;
      if (this.option === 'all') return (this.prepaid / 1.2 / this.price!) * 100;
      return 0;
    },
    fullPrepaidFeeTotal(): number {
      return this.price! * (this.rate_all / 100) * 1.2;
    },
    nsnfFeeTotal(): number {
      return this.price! * (this.rate_nsnf / 100) * 1.2;
    },
    actualSavings(): number {
      return this.nsnfFeeTotal - this.total;
    },
    possibleExtraSavings(): number {
      return this.total - this.fullPrepaidFeeTotal;
    },
    optionLabel(): string {
      return this.option === 'nsnf' ? 'No Sale No Fee' : this.option === 'part' ? 'Part Prepaid' : 'Full Prepaid';
    },
  },

  methods: {
    getBracketRate(price: number, type: 'nsnf' | 'part' | 'all'): number {
      if (price < 70000) price = 70000;
      if (price > 220000) {
        if (type === 'nsnf') return 1.8;
        if (type === 'part') return 1.44;
        if (type === 'all') return 1.15;
      }
      for (let i = this.brackets.length - 1; i >= 0; i--) {
        if (price >= this.brackets[i].min) return this.brackets[i][type];
      }
      return 0;
    },

    toggleAdditionalInput(): void {
      if (this.option !== 'part') this.additionalPrepaid = 0;
      this.showResults = false;
    },

    calculate(): void {
      if (!this.validPrice) return;

      if (this.mode === 'v2') {
        const conv = (this.conversion || 100) / 100;
        this.v2Results = this.v2Options.map((opt) => {
          const total = (opt.fixed + opt.percent * this.price!) * conv;
          return { ...opt, total };
        });
      }

      this.showResults = true;
    },
  },
});
</script>

<style scoped>
.calculator-container {
  margin: 0 auto;
  background: white;
  border-radius: var(--rounded);
}

.calculator-header {
  text-align: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}

.calculator-header h2 {
  margin: 0;
  color: var(--c-secondary);
  font-size: 1.5rem;
  text-align: start;
}

.inputs-section {
  margin-bottom: 2rem;
}

.field-group {
  margin-bottom: 1.5rem;
}

.field-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--c-secondary);
}

.calculator-input,
.calculator-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  background: white;
  transition: all 0.2s ease;
  border-radius: var(--rounded);
}

.calculator-input:focus,
.calculator-select:focus {
  outline: none;
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.additional-input {
  background: #f9fafb;
  border-radius: var(--rounded);
  margin-top: 1rem;
}

.calculate-section {
  margin-top: 2rem;
  text-align: start;
}

.calculate-button {
  background: var(--c-primary);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: var(--rounded);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
}

.calculate-button:hover:not(:disabled) {
  background: var(--c-primary-dark, #2563eb);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.calculate-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.results-section {
  margin-top: 2rem;
}

.results-table {
  background: white;
  border-radius: var(--rounded);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.table-row:last-child {
  border-bottom: none;
}

.header-row {
  background: var(--c-primary);
  color: white;
}

.total-row {
  background: #f9fafb;
  color: var(--c-primary);
  border-top: 2px solid var(--c-primary);
  border-bottom: 2px solid var(--c-primary);
}

.best-option-row {
  background: #ecfdf5;
  color: #047857;
}

.cell {
  flex: 1;
}

.text-right {
  text-align: right;
}

.savings-message {
  padding: 1.5rem;
  margin-top: 1.5rem;
  border-radius: var(--rounded);
  line-height: 1.6;
}

.part-savings {
  background: #eff6ff;
  color: #1e40af;
}

.nsnf-message {
  background: #fef3c7;
  color: #92400e;
}

.optimal-message {
  background: #ecfdf5;
  color: #047857;
  text-align: center;
}

.error-message {
  padding: 1.5rem;
  background: #fef2f2;
  color: #dc2626;
  border-radius: var(--rounded);
  text-align: center;
  margin-top: 1rem;
}

@media (max-width: 640px) {
  .table-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem;
  }

  .text-right {
    text-align: left;
  }
}
</style>
